import type { AuthUser } from '@/types/auth.types'
import type { SalesLeadUserRef } from '@/modules/sales/services/sales-lead.service'
import { fullName } from '@/utils/format'
import { canAccess } from '@/utils/permission.utils'

type LeadAccessRef = {
  assignedUserId: string
  createdById?: string
}

/** Gated by the sales_leads.view_all permission — a real, per-role togglable
 * key (Permissions page), not hard-coded to Manager. Super Admin bypasses
 * via canAccess regardless of the permission map. */
export function hasOrgWideLeadScope(user: AuthUser | null | undefined): boolean {
  if (!user) return false
  return canAccess(user.role, user.permissions, 'sales_leads.view_all')
}

/** Assistant and Manager always pick on create. Sales agent needs Assign.
 * Changing Assigned to later still requires Assign (see canChangeLeadAssignee). */
export function canPickAssigneeOnCreate(
  user: AuthUser | null | undefined,
  canAssign: boolean,
): boolean {
  if (!user) return false
  if (user.role === 'assistant' || hasOrgWideLeadScope(user)) return true
  return canAssign
}

export function isLeadInScope(
  user: AuthUser | null | undefined,
  lead: LeadAccessRef | null | undefined,
): boolean {
  if (!user || !lead) return false
  if (hasOrgWideLeadScope(user)) return true
  if (lead.assignedUserId === user.id) return true
  if (user.role === 'salesman') return false
  return Boolean(lead.createdById && lead.createdById === user.id)
}

/** Assign permission required to change Assigned to after create.
 * Manager / assistant: any query they can already see. Sales agent: never. */
export function canChangeLeadAssignee(
  user: AuthUser | null | undefined,
  lead: LeadAccessRef | null | undefined,
  canAssign: boolean,
): boolean {
  if (!user || !lead || !canAssign) return false
  if (user.role === 'salesman') return false
  if (hasOrgWideLeadScope(user)) return true
  return isLeadInScope(user, lead)
}

/** `sales_leads.delete` on any query the actor can already see. */
export function canDeleteLead(
  user: AuthUser | null | undefined,
  lead: LeadAccessRef | null | undefined,
  canDelete: boolean,
): boolean {
  if (!canDelete) return false
  return isLeadInScope(user, lead)
}

/** Old-customer leads keep city and province. The sales region stays empty. */
export function isOldCustomerLeadSource(value: string | null | undefined): boolean {
  return (value ?? '').trim().toLowerCase() === 'old customer'
}

/** Combined old-customer sheet names stay together on the lead. One agent still uses the user name. */
export function leadAgentLabel(
  agentLabel: string | null | undefined,
  assigned: { id: string; firstName: string; lastName: string },
  currentUserId?: string | null,
): string {
  const sheet = agentLabel?.trim()
  if (sheet && sheet.includes('/')) return sheet
  return assigneeOptionLabel(assigned, currentUserId)
}

export function assigneeOptionLabel(
  a: { id: string; firstName: string; lastName: string },
  currentUserId?: string | null,
): string {
  const name = fullName(a.firstName, a.lastName)
  return a.id === currentUserId ? `${name} (you)` : name
}

/** Picker list from the API. Keep `extra` so an already-assigned person still shows on edit. */
export function assigneePickerOptions(
  assignees: SalesLeadUserRef[] | undefined,
  currentUser: AuthUser | null | undefined,
  extra?: SalesLeadUserRef | null,
): SalesLeadUserRef[] {
  const byId = new Map<string, SalesLeadUserRef>()
  for (const a of assignees ?? []) byId.set(a.id, a)
  if (extra) byId.set(extra.id, extra)
  const list = [...byId.values()]
  const currentId = currentUser?.id
  list.sort((a, b) => {
    if (a.id === currentId) return -1
    if (b.id === currentId) return 1
    return fullName(a.firstName, a.lastName).localeCompare(fullName(b.firstName, b.lastName))
  })
  return list
}
